import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import {
  FiAlertCircle,
  FiCheck,
  FiImage,
  FiSearch,
  FiTrash,
  FiUpload,
  FiX,
} from 'react-icons/fi';
import {
  Badge,
  Button,
  Field,
  Input,
  Textarea,
  cn,
  toast,
} from '../ui';
import Modal from '../Modal';
import {
  AI_COLOR_BADGES,
  aiMonthName,
  formatAiBudget,
  formatPercent,
  formatUsd,
  getAiSpend,
  getAiSpendDetail,
  isAiSpendMigrationMissing,
  AI_SPEND_MIGRATION_MESSAGE,
  unlockAiSpend,
} from '../../servicesV2/aiSpendService';
import {
  ALL_CLIENT_MODULES,
  CLIENT_COLORS,
  CLIENT_FORM_SECTIONS,
  CLIENT_MODULE_GROUPS,
  CUSTOM_MODULES_CODE,
  moduleLabel,
} from './clientFormConfig';

/* -------------------------------------------------------------------------- */
/*  Utilities                                                                 */
/* -------------------------------------------------------------------------- */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function toDate(val) {
  if (!val) return null;
  if (val instanceof Date) return val;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* -------------------------------------------------------------------------- */
/*  Anchor nav with scroll-spy                                                */
/* -------------------------------------------------------------------------- */

function useScrollSpy(sectionIds, rootRef) {
  const [active, setActive] = useState(sectionIds[0]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const sections = sectionIds
      .map((id) => document.getElementById(`cf-section-${id}`))
      .filter(Boolean);

    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].target.id.replace('cf-section-', '');
          setActive(id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [sectionIds, rootRef]);

  return active;
}

function AnchorNav({ activeId, onSelect, sections, modulesCount }) {
  return (
    <nav className="sticky top-[72px]">
      <p className="px-2 pb-2 text-micro text-fg-subtle">Sections</p>
      <ul className="flex flex-col gap-0.5">
        {sections.map((s) => {
          const active = s.id === activeId;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSelect(s.id)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-md px-2 h-8 text-body-sm transition-colors',
                  active
                    ? 'bg-brand-50 text-brand-700 font-semibold'
                    : 'text-fg-muted hover:bg-ink-50 hover:text-fg',
                )}
              >
                <span>{s.label}</span>
                {s.id === 'modules' && modulesCount > 0 && (
                  <Badge variant={active ? 'brand' : 'neutral'} size="sm">
                    {modulesCount}
                  </Badge>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/* -------------------------------------------------------------------------- */
/*  Module picker                                                             */
/* -------------------------------------------------------------------------- */

function ModulePicker({ selected, onToggle, onSelectAll, onClearAll }) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query.trim()) return CLIENT_MODULE_GROUPS;
    const q = query.trim().toLowerCase();
    return CLIENT_MODULE_GROUPS.map((g) => ({
      ...g,
      modules: g.modules.filter(
        (m) =>
          m.label.toLowerCase().includes(q) || m.id.toLowerCase().includes(q),
      ),
    })).filter((g) => g.modules.length > 0);
  }, [query]);

  const hasResults = filtered.some((g) => g.modules.length > 0);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un module…"
          leading={<FiSearch size={13} />}
          containerClassName="max-w-xs w-full"
        />
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={onSelectAll}>
            Tout cocher
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onClearAll}>
            Tout décocher
          </Button>
        </div>
      </div>

      {!hasResults ? (
        <p className="rounded-md border border-dashed border-line bg-surface-muted p-6 text-center text-body-sm text-fg-subtle">
          Aucun module ne correspond à « {query} ».
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {filtered.map((group) => (
            <div key={group.id}>
              <div className="mb-1.5 flex items-center gap-2">
                <p className="text-micro text-fg-subtle">{group.label}</p>
                <div className="h-px flex-1 bg-line" />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {group.modules.map((m) => {
                  const on = selected.includes(m.id);
                  return (
                    <label
                      key={m.id}
                      className={cn(
                        'flex cursor-pointer items-center justify-between gap-3',
                        'rounded-md border px-3 py-2 transition-colors',
                        on
                          ? 'border-brand-200 bg-brand-50'
                          : 'border-line bg-surface hover:border-line-strong hover:bg-ink-50',
                      )}
                    >
                      <div className="min-w-0">
                        <p
                          className={cn(
                            'truncate text-body-sm',
                            on ? 'font-medium text-fg' : 'text-fg',
                          )}
                        >
                          {m.label}
                        </p>
                        <p className="truncate font-mono text-[10px] text-fg-subtle">
                          {m.id}
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => onToggle(m.id)}
                        className="peer sr-only"
                      />
                      <span
                        aria-hidden
                        className={cn(
                          'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
                          on
                            ? 'border-brand-600 bg-brand-600 text-white'
                            : 'border-line-strong bg-surface',
                        )}
                      >
                        {on && <FiCheck size={12} />}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Licence                                                                   */
/* -------------------------------------------------------------------------- */

/** Badge for the stored licence status (backend `license` on show-byId). */
function LicenseStatusBadge({ license }) {
  if (!license) return null;
  const { reason, daysLeft, lock } = license;
  if (reason === 'unlimited') return <Badge variant="neutral" dot>Illimité</Badge>;
  if (reason === 'bad_signature') {
    return <Badge variant="danger" dot>Signature invalide — enregistrez pour re-signer</Badge>;
  }
  if (reason === 'expired') {
    return <Badge variant="danger" dot>{lock === 'partial' ? 'Expiré · modules conservés actifs' : 'Expiré · application bloquée'}</Badge>;
  }
  if (reason === 'not_started') return <Badge variant="warning" dot>Pas encore commencé</Badge>;
  if (daysLeft === null || daysLeft === undefined) return <Badge variant="success" dot>Valide · sans fin</Badge>;
  if (daysLeft <= 30) return <Badge variant="warning" dot>Expire dans {daysLeft} j</Badge>;
  return <Badge variant="success" dot>Valide · {daysLeft} jours restants</Badge>;
}

function ToggleRow({ title, description, checked, onChange }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-line bg-surface px-3 py-2.5 transition-colors hover:border-line-strong">
      <div className="min-w-0">
        <p className="text-body-sm font-medium text-fg">{title}</p>
        <p className="mt-0.5 text-caption text-fg-subtle">{description}</p>
      </div>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
          checked ? 'bg-brand-600' : 'bg-ink-200',
        )}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0.5',
          )}
        />
      </span>
    </label>
  );
}

/**
 * Licence controls (cloud clients). Unlimited = no signature, the app works even
 * after the end date. Limited = the backend signs the dates + expiry behaviour
 * on save: block the whole app, or keep the chosen modules working.
 */
function LicensePanel({ control, unlimited, expiryMode, enabledModules, keptModules, onToggleKept }) {
  const keepable = [...enabledModules, CUSTOM_MODULES_CODE];
  return (
    <div className="flex flex-col gap-3 border-t border-line pt-4">
      <Controller
        control={control}
        name="licenseUnlimited"
        render={({ field }) => (
          <ToggleRow
            title="Contrat illimité"
            description="L'application reste accessible même après la date de fin."
            checked={field.value}
            onChange={field.onChange}
          />
        )}
      />

      {!unlimited && (
        <Field label="À l'expiration de la licence">
          <Controller
            control={control}
            name="licenseExpiryMode"
            render={({ field }) => (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {[
                  { value: 'full', title: "Bloquer toute l'application", hint: 'Les utilisateurs voient « Licence expirée ».' },
                  { value: 'partial', title: 'Garder certains modules', hint: 'Les autres modules affichent « Module verrouillé ».' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={cn(
                      'flex cursor-pointer flex-col gap-0.5 rounded-md border px-3 py-2 transition-colors',
                      field.value === opt.value
                        ? 'border-brand-200 bg-brand-50'
                        : 'border-line bg-surface hover:border-line-strong hover:bg-ink-50',
                    )}
                  >
                    <input
                      type="radio"
                      className="sr-only"
                      checked={field.value === opt.value}
                      onChange={() => field.onChange(opt.value)}
                    />
                    <span className="text-body-sm font-medium text-fg">{opt.title}</span>
                    <span className="text-caption text-fg-subtle">{opt.hint}</span>
                  </label>
                ))}
              </div>
            )}
          />
        </Field>
      )}

      {!unlimited && expiryMode === 'partial' && (
        <Field
          label="Modules qui restent actifs après l'expiration"
          hint="Seuls les modules activés pour ce client peuvent être conservés."
          error={keptModules.length === 0 ? 'Choisissez au moins un module.' : undefined}
        >
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {keepable.map((code) => {
              const on = keptModules.includes(code);
              return (
                <label
                  key={code}
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2 transition-colors',
                    on ? 'border-brand-200 bg-brand-50' : 'border-line bg-surface hover:border-line-strong hover:bg-ink-50',
                  )}
                >
                  <span className="truncate text-body-sm text-fg">{moduleLabel(code)}</span>
                  <input type="checkbox" className="sr-only" checked={on} onChange={() => onToggleKept(code)} />
                  <span
                    aria-hidden
                    className={cn(
                      'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
                      on ? 'border-brand-600 bg-brand-600 text-white' : 'border-line-strong bg-surface',
                    )}
                  >
                    {on && <FiCheck size={12} />}
                  </span>
                </label>
              );
            })}
          </div>
        </Field>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Color picker                                                              */
/* -------------------------------------------------------------------------- */

function ColorPicker({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {CLIENT_COLORS.map((c) => {
        const on = c.name === value;
        return (
          <button
            key={c.name}
            type="button"
            onClick={() => onChange(c.name)}
            className={cn(
              'relative inline-flex items-center gap-2 rounded-md border px-3 h-9',
              'text-body-sm text-white transition-all',
              'focus-visible:outline-none focus-visible:shadow-focus-ring',
              on ? 'ring-2 ring-offset-2 ring-offset-surface ring-fg' : 'hover:scale-[1.02]',
            )}
            style={{ backgroundColor: c.hex }}
          >
            {on && <FiCheck size={14} />}
            <span className="font-medium">{c.name}</span>
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Logo upload                                                               */
/* -------------------------------------------------------------------------- */

function LogoUpload({ existingUrl, file, onFile, onClearExisting }) {
  const previewUrl = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    return existingUrl || null;
  }, [file, existingUrl]);

  useEffect(() => {
    return () => {
      if (file && previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [file, previewUrl]);

  if (previewUrl) {
    return (
      <div className="flex items-center gap-4 rounded-md border border-line bg-surface-muted p-3">
        <div className="flex h-16 w-16 items-center justify-center rounded-md border border-line bg-surface">
          <img
            src={previewUrl}
            alt="Logo"
            className="max-h-full max-w-full object-contain"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-body-sm font-medium text-fg">
            {file ? file.name : 'Logo existant'}
          </p>
          <p className="mt-0.5 text-caption text-fg-subtle">
            {file ? `${Math.round(file.size / 1024)} KB · prêt à être envoyé` : 'Chargé depuis le serveur'}
          </p>
        </div>
        <Button
          type="button"
          variant="danger-ghost"
          size="sm"
          iconLeft={<FiTrash size={13} />}
          onClick={() => {
            onFile(null);
            onClearExisting?.();
          }}
        >
          Retirer
        </Button>
      </div>
    );
  }

  return (
    <label
      className={cn(
        'flex flex-col items-center justify-center gap-1.5',
        'cursor-pointer rounded-md border-2 border-dashed border-line bg-surface-muted',
        'px-4 py-6 transition-colors hover:border-line-strong hover:bg-ink-50',
      )}
    >
      <FiUpload size={20} className="text-fg-muted" />
      <p className="text-body-sm text-fg">Déposer un logo ou cliquer pour choisir</p>
      <p className="text-caption text-fg-subtle">PNG, JPG — 2 MB max.</p>
      <input
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/*  ClientForm                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Shared form for creating / editing a client tenant.
 *
 *   <ClientForm
 *     mode="create" | "edit" | "from-request"
 *     initialValues={{ Nom, Key, ... , modules: [...], color: 'Sky' }}
 *     initialLogoUrl={url}
 *     disabledFields={['Key']}
 *     showColor={true}
 *     submitLabel="Enregistrer"
 *     onSubmit={async ({ data, logo, modules, color }) => { ... }}
 *     onCancel={...}
 *     errorText={...}
 *     loading={...}
 *     summary={<div>...</div>}
 *   />
 */
export function ClientForm({
  mode = 'create',
  initialValues = {},
  initialLogoUrl = null,
  disabledFields = [],
  showColor = true,
  submitLabel = 'Enregistrer',
  cancelLabel = 'Annuler',
  onSubmit,
  onCancel,
  errorText,
  loading,
  summary,
}) {
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      Nom: '',
      Key: '',
      representantName: '',
      representantEmail: '',
      RaisonSocial: '',
      Activite: '',
      DomainApplication: '',
      JustificationExclusion: '',
      isOnPremise: false,
      publicApiUrl: '',
      whatsappMonthlyBudgetUsd: '',
      licenseUnlimited: false,
      licenseExpiryMode: 'full',
      aiMonthlyBudgetUsd: '',
      webSearchEnabled: true,
      webSearchNewsName: '',
      ...initialValues,
      startDate: toDate(initialValues.startDate),
      endDate: toDate(initialValues.endDate),
    },
  });

  // Re-seed form when initialValues arrive async (edit / from-request).
  const seededRef = useRef(false);
  useEffect(() => {
    if (!initialValues || Object.keys(initialValues).length === 0) return;
    if (seededRef.current) return;
    reset({
      Nom: '',
      Key: '',
      representantName: '',
      representantEmail: '',
      RaisonSocial: '',
      Activite: '',
      DomainApplication: '',
      JustificationExclusion: '',
      isOnPremise: false,
      publicApiUrl: '',
      whatsappMonthlyBudgetUsd: '',
      licenseUnlimited: false,
      licenseExpiryMode: 'full',
      aiMonthlyBudgetUsd: '',
      webSearchEnabled: true,
      webSearchNewsName: '',
      ...initialValues,
      startDate: toDate(initialValues.startDate),
      endDate: toDate(initialValues.endDate),
    });
    seededRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialValues)]);

  const [selectedModules, setSelectedModules] = useState(initialValues.modules || []);
  const [keptModules, setKeptModules] = useState(initialValues.licenseKeptModules || []);
  const [color, setColor] = useState(initialValues.color || '');
  const [logo, setLogo] = useState(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState(initialValues.logoUrl || null);

  useEffect(() => {
    if (Array.isArray(initialValues.modules) && selectedModules.length === 0) {
      setSelectedModules(initialValues.modules);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialValues.modules)]);
  useEffect(() => {
    if (Array.isArray(initialValues.licenseKeptModules) && keptModules.length === 0) {
      setKeptModules(initialValues.licenseKeptModules);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialValues.licenseKeptModules)]);
  // A module disabled for the client can no longer be kept after expiry.
  useEffect(() => {
    setKeptModules((prev) => prev.filter((m) => m === CUSTOM_MODULES_CODE || selectedModules.includes(m)));
  }, [selectedModules]);
  useEffect(() => {
    if (initialValues.color && !color) setColor(initialValues.color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues.color]);
  useEffect(() => {
    if (initialLogoUrl && !existingLogoUrl) setExistingLogoUrl(initialLogoUrl);
  }, [initialLogoUrl, existingLogoUrl]);

  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const email = watch('representantEmail');
  const emailValid = email ? EMAIL_REGEX.test(email) : false;
  const isOnPremise = watch('isOnPremise');
  const licenseUnlimited = watch('licenseUnlimited');
  const licenseExpiryMode = watch('licenseExpiryMode');

  const toggleKept = useCallback((code) => {
    setKeptModules((prev) => (prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]));
  }, []);

  const scrollRootRef = useRef(null);
  const activeSection = useScrollSpy(
    CLIENT_FORM_SECTIONS.map((s) => s.id),
    scrollRootRef,
  );

  const toggleModule = useCallback((id) => {
    setSelectedModules((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);
  const selectAllModules = () =>
    setSelectedModules(ALL_CLIENT_MODULES.map((m) => m.id));
  const clearAllModules = () => setSelectedModules([]);

  const jumpTo = (id) => {
    const el = document.getElementById(`cf-section-${id}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = async (data) => {
    await onSubmit?.({
      data,
      modules: selectedModules,
      keptModules,
      color,
      logo,
      clearedExistingLogo: Boolean(initialLogoUrl && !existingLogoUrl && !logo),
    });
  };

  const req = 'Champ requis';

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className="grid grid-cols-1 gap-8 overflow-visible lg:grid-cols-[220px_minmax(0,1fr)]"
    >
      {/* Anchor rail */}
      <aside className="hidden overflow-visible lg:block">
        {summary && <div className="mb-4">{summary}</div>}
        <AnchorNav
          activeId={activeSection}
          onSelect={jumpTo}
          sections={CLIENT_FORM_SECTIONS}
          modulesCount={selectedModules.length}
        />
      </aside>

      {/* Form column */}
      <div ref={scrollRootRef} className="min-w-0 overflow-visible">
        {summary && <div className="lg:hidden mb-4">{summary}</div>}

        {errorText && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-danger-200 bg-danger-50 px-3 py-2">
            <FiAlertCircle size={16} className="mt-0.5 text-danger-600" />
            <p className="text-body-sm text-danger-700">{errorText}</p>
          </div>
        )}

        {/* General */}
        <Section id="general" title="Général" description="Identité du tenant.">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field
              className="sm:col-span-2"
              label="Nom"
              required
              error={errors.Nom && req}
            >
              <Input
                placeholder="Betterfly"
                invalid={!!errors.Nom}
                {...register('Nom', { required: true })}
              />
            </Field>
            <Field
              label="Key"
              required
              hint={
                disabledFields.includes('Key') ? 'La clé ne peut pas être modifiée.' : undefined
              }
              error={errors.Key && req}
            >
              <Input
                placeholder="BFL"
                className="font-mono"
                disabled={disabledFields.includes('Key')}
                invalid={!!errors.Key}
                {...register('Key', {
                  required: !disabledFields.includes('Key'),
                })}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Raison sociale"
              required
              error={errors.RaisonSocial && req}
            >
              <Input
                invalid={!!errors.RaisonSocial}
                {...register('RaisonSocial', { required: true })}
              />
            </Field>
            <Field label="Activité" required error={errors.Activite && req}>
              <Input
                invalid={!!errors.Activite}
                {...register('Activite', { required: true })}
              />
            </Field>
          </div>

          <Field
            label="Domaine d'application"
            required
            error={errors.DomainApplication && req}
          >
            <Input
              invalid={!!errors.DomainApplication}
              {...register('DomainApplication', { required: true })}
            />
          </Field>

          <Field
            label="Justification de l'exclusion"
            hint="Motif d'exclusion de certaines exigences du système de management."
          >
            <Textarea rows={3} {...register('JustificationExclusion')} />
          </Field>

          <Controller
            control={control}
            name="isOnPremise"
            render={({ field }) => (
              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-line bg-surface px-3 py-2.5 transition-colors hover:border-line-strong">
                <div className="min-w-0">
                  <p className="text-body-sm font-medium text-fg">Client on-premise</p>
                  <p className="mt-0.5 text-caption text-fg-subtle">
                    Le client héberge son instance sur sa propre infrastructure.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="peer sr-only"
                />
                <span
                  aria-hidden
                  className={cn(
                    'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
                    field.value ? 'bg-brand-600' : 'bg-ink-200',
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                      field.value ? 'translate-x-4' : 'translate-x-0.5',
                    )}
                  />
                </span>
              </label>
            )}
          />

          {isOnPremise && (
            <Field
              label="Client public API URL"
              required
              error={errors.publicApiUrl && req}
            >
              <Input
                type="url"
                placeholder="https://api.client.com"
                invalid={!!errors.publicApiUrl}
                {...register('publicApiUrl', { required: isOnPremise })}
              />
            </Field>
          )}
        </Section>

        {/* Contract */}
        <Section
          id="contract"
          title="Contrat"
          description="Période de validité du contrat et licence du client."
          aside={!isOnPremise && <LicenseStatusBadge license={initialValues.license} />}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Date de début" required error={errors.startDate && req}>
              <Controller
                control={control}
                name="startDate"
                rules={{ required: true }}
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={field.onChange}
                    isClearable
                    dateFormat="dd/MM/yyyy"
                    maxDate={endDate}
                    placeholderText="jj/mm/aaaa"
                    wrapperClassName="w-full"
                    className={cn(
                      'w-full h-8 rounded-md border border-line bg-surface px-3 text-body-sm text-fg placeholder:text-fg-subtle',
                      'focus-visible:outline-none focus-visible:border-brand-500 focus-visible:shadow-focus-ring',
                      errors.startDate && 'border-danger-500',
                    )}
                  />
                )}
              />
            </Field>
            <Field label="Date de fin" required error={errors.endDate && req}>
              <Controller
                control={control}
                name="endDate"
                rules={{ required: true }}
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={field.onChange}
                    isClearable
                    dateFormat="dd/MM/yyyy"
                    minDate={startDate}
                    placeholderText="jj/mm/aaaa"
                    wrapperClassName="w-full"
                    className={cn(
                      'w-full h-8 rounded-md border border-line bg-surface px-3 text-body-sm text-fg placeholder:text-fg-subtle',
                      'focus-visible:outline-none focus-visible:border-brand-500 focus-visible:shadow-focus-ring',
                      errors.endDate && 'border-danger-500',
                    )}
                  />
                )}
              />
            </Field>
          </div>
          {startDate && endDate && endDate > startDate && (
            <p className="text-caption text-fg-subtle">
              Durée:{' '}
              <span className="text-fg">
                {Math.round(
                  (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
                )}{' '}
                jours
              </span>
            </p>
          )}
          {isOnPremise ? (
            <p className="rounded-md border border-dashed border-line bg-surface-muted px-3 py-2.5 text-caption text-fg-muted">
              Licence gérée par script on-premise : elle est signée hors ligne et installée dans la base du client.
            </p>
          ) : (
            <LicensePanel
              control={control}
              unlimited={licenseUnlimited}
              expiryMode={licenseExpiryMode}
              enabledModules={selectedModules}
              keptModules={keptModules}
              onToggleKept={toggleKept}
            />
          )}
        </Section>

        {/* Representative */}
        <Section id="representative" title="Représentant" description="Point de contact côté client.">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Nom du représentant"
              required
              error={errors.representantName && req}
            >
              <Input
                invalid={!!errors.representantName}
                {...register('representantName', { required: true })}
              />
            </Field>
            <Field
              label="Email"
              required
              error={
                errors.representantEmail
                  ? errors.representantEmail.type === 'required'
                    ? req
                    : 'Email invalide'
                  : undefined
              }
            >
              <Input
                type="email"
                invalid={!!errors.representantEmail}
                trailing={
                  email ? (
                    emailValid ? (
                      <FiCheck size={14} className="text-success-600" />
                    ) : (
                      <FiX size={14} className="text-danger-600" />
                    )
                  ) : null
                }
                {...register('representantEmail', {
                  required: true,
                  pattern: EMAIL_REGEX,
                })}
              />
            </Field>
          </div>
        </Section>

        {/* Modules */}
        <Section
          id="modules"
          title="Modules"
          description={`Modules activés pour ce tenant · ${selectedModules.length} sélectionné${selectedModules.length > 1 ? 's' : ''}.`}
        >
          <ModulePicker
            selected={selectedModules}
            onToggle={toggleModule}
            onSelectAll={selectAllModules}
            onClearAll={clearAllModules}
          />
          {/* Creation does not carry the budget (20 $ by default); it is set from the client's edit page. */}
          {mode === 'edit' && selectedModules.includes('WHATSAPP') && (
            <Field
              label="Budget WhatsApp mensuel ($)"
              hint="Au-delà, WhatsApp invite poliment à poursuivre sur l'écran jusqu'au 1er du mois suivant. Vide : 20 $."
              error={errors.whatsappMonthlyBudgetUsd && 'Montant en dollars, par exemple 20 ou 0,05'}
            >
              <Input
                type="text"
                inputMode="decimal"
                placeholder="20"
                className="tabular-nums"
                invalid={!!errors.whatsappMonthlyBudgetUsd}
                {...register('whatsappMonthlyBudgetUsd', {
                  validate: (value) => !value || /^\d+([.,]\d{1,2})?$/.test(String(value).trim()),
                })}
              />
            </Field>
          )}
          {/* E7: monthly AI threshold in dollars, for every client; edit mode only (set after creation). */}
          {mode === 'edit' && (
            <>
              <Field
                label="Seuil IA mensuel ($)"
                hint="Vide : pas de seuil (alerte de veille à 30 $). 0 : IA coupée ce mois-ci."
                error={errors.aiMonthlyBudgetUsd && 'Montant en dollars, par exemple 50 ou 12,5'}
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="Aucun seuil"
                  className="tabular-nums"
                  invalid={!!errors.aiMonthlyBudgetUsd}
                  {...register('aiMonthlyBudgetUsd', {
                    validate: (value) => !value || /^\d+([.,]\d{1,2})?$/.test(String(value).trim()),
                  })}
                />
              </Field>
              {/* SWOT lot 3b: BetterAssist web search. On by default; off and greyed for an on-premise client. */}
              <Controller
                control={control}
                name="webSearchEnabled"
                render={({ field }) => {
                  const checked = !isOnPremise && !!field.value;
                  return (
                    <label
                      className={cn(
                        'flex items-center justify-between gap-3 rounded-md border border-line bg-surface px-3 py-2.5 transition-colors',
                        isOnPremise
                          ? 'cursor-not-allowed opacity-60'
                          : 'cursor-pointer hover:border-line-strong',
                      )}
                    >
                      <div className="min-w-0">
                        <p className="text-body-sm font-medium text-fg">Recherche web pour BetterAssist</p>
                        <p className="mt-0.5 text-caption text-fg-subtle">
                          Les requêtes partent chez Microsoft Bing, hors du cadre Azure. Elles ne contiennent aucune donnée du client ; seul le nom recherché part, seul.
                        </p>
                        {isOnPremise && (
                          <p className="mt-0.5 text-caption text-fg-subtle">
                            Indisponible pour un client on-premise.
                          </p>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        aria-label="Recherche web pour BetterAssist"
                        checked={checked}
                        disabled={isOnPremise}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="peer sr-only"
                      />
                      <span
                        aria-hidden
                        className={cn(
                          'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
                          checked ? 'bg-brand-600' : 'bg-ink-200',
                        )}
                      >
                        <span
                          className={cn(
                            'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                            checked ? 'translate-x-4' : 'translate-x-0.5',
                          )}
                        />
                      </span>
                    </label>
                  );
                }}
              />
              <Field
                label="Nom recherché dans l’actualité"
                hint="À renseigner à la main ; jamais pré-rempli."
                error={errors.webSearchNewsName && '120 caractères au plus'}
              >
                <Input
                  type="text"
                  maxLength={120}
                  aria-label="Nom recherché dans l’actualité"
                  invalid={!!errors.webSearchNewsName}
                  {...register('webSearchNewsName', { maxLength: 120 })}
                />
              </Field>
              <AiSpendPanel clientKey={initialValues.Key} />
            </>
          )}
        </Section>

        {/* Branding */}
        <Section id="branding" title="Identité" description="Logo et couleur du thème.">
          <Field label="Logo">
            <LogoUpload
              existingUrl={existingLogoUrl}
              file={logo}
              onFile={setLogo}
              onClearExisting={() => setExistingLogoUrl(null)}
            />
          </Field>

          {showColor && (
            <Field
              label="Couleur du thème"
              hint="Influence l'accent visuel dans l'application du client."
            >
              <ColorPicker value={color} onChange={setColor} />
            </Field>
          )}

          {!showColor && (
            <div className="flex items-center gap-2 rounded-md border border-dashed border-line bg-surface-muted px-3 py-2.5 text-caption text-fg-muted">
              <FiImage size={13} />
              La couleur du thème est définie à la création et ne peut pas être modifiée.
            </div>
          )}
        </Section>

        {/* Sticky footer — sits inside the scroll surface of <main>. */}
        <div
          className={cn(
            'sticky bottom-0 z-10 mt-6',
            'rounded-lg border border-line bg-surface/95 px-4 py-3 shadow-e2 backdrop-blur',
            'flex items-center justify-between gap-3',
          )}
        >
          <p className="text-caption text-fg-subtle">
            {isDirty ? 'Modifications non enregistrées' : 'Aucune modification'}
          </p>
          <div className="flex items-center gap-2">
            <Button type="button" variant="secondary" onClick={onCancel}>
              {cancelLabel}
            </Button>
            <Button type="submit" loading={loading}>
              {submitLabel}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section wrapper                                                           */
/* -------------------------------------------------------------------------- */

/**
 * AiSpendPanel — current month AI spend of the client (saved threshold, not the one being typed),
 * unlock button when red, and the last 6 months.
 */
function AiSpendPanel({ clientKey }) {
  const [row, setRow] = useState(null);
  const [detail, setDetail] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | migration | failed
  const [confirming, setConfirming] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const load = useCallback(() => {
    if (!clientKey) return Promise.resolve();
    return Promise.all([getAiSpend(), getAiSpendDetail(clientKey)]).then(
      ([list, one]) => {
        setRow(list.rows.find((item) => item.key === clientKey) || null);
        setDetail(one);
        setState('ready');
      },
      (error) => setState(isAiSpendMigrationMissing(error) ? 'migration' : 'failed'),
    );
  }, [clientKey]);

  useEffect(() => {
    setState('loading');
    load();
  }, [load]);

  const unlock = async () => {
    setUnlocking(true);
    try {
      await unlockAiSpend(clientKey);
      toast.success('IA débloquée jusqu\'à la fin du mois', { description: clientKey });
      setConfirming(false);
      await load();
    } catch (error) {
      toast.error('Échec du déblocage', {
        description: isAiSpendMigrationMissing(error) ? AI_SPEND_MIGRATION_MESSAGE : error?.message,
      });
    } finally {
      setUnlocking(false);
    }
  };

  const badge = row && AI_COLOR_BADGES[row.color];

  return (
    <div className="rounded-md border border-line bg-surface-muted p-4">
      <p className="text-micro text-fg-subtle">Consommation IA du mois</p>
      {state === 'loading' && <p className="mt-1 text-caption text-fg-muted">Lecture en cours…</p>}
      {state === 'migration' && <p className="mt-1 text-caption text-fg-muted">{AI_SPEND_MIGRATION_MESSAGE}</p>}
      {state === 'failed' && <p className="mt-1 text-caption text-fg-muted">Consommation indisponible.</p>}
      {state === 'ready' && !row && (
        <p className="mt-1 text-caption text-fg-muted">Aucune lecture de consommation pour ce client pour le moment.</p>
      )}
      {state === 'ready' && row && (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2">
            <div>
              <p className="text-caption text-fg-subtle">Dépensé en {aiMonthName(row.month)}</p>
              <p className="text-body-sm font-semibold tabular-nums text-fg">{formatUsd(row.spentUsd)}</p>
            </div>
            <div>
              <p className="text-caption text-fg-subtle">Seuil</p>
              <p className="text-body-sm font-semibold tabular-nums text-fg">{formatAiBudget(row.budgetUsd)}</p>
            </div>
            <div>
              <p className="text-caption text-fg-subtle">Avancement</p>
              <p className="text-body-sm font-semibold tabular-nums text-fg">{formatPercent(row.percent)}</p>
            </div>
            <div>
              {badge && (
                <Badge variant={badge.variant} dot>
                  {badge.label}
                </Badge>
              )}
            </div>
            {row.color === 'red' && (
              <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
                Débloquer jusqu'à la fin du mois
              </Button>
            )}
          </div>
          {row.unlockedThisMonth && (
            <p className="mt-2 text-caption text-fg-muted">Débloqué jusqu'à la fin du mois : l'IA n'est pas coupée.</p>
          )}
          {detail?.history?.length > 0 && (
            <table className="mt-3 w-full max-w-sm text-caption">
              <thead>
                <tr className="text-left text-fg-subtle">
                  <th className="py-1 pr-4 font-medium">Mois</th>
                  <th className="py-1 text-right font-medium">Dépensé</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {detail.history.map((item) => (
                  <tr key={item.month}>
                    <td className="py-1 pr-4 capitalize text-fg-muted">{aiMonthName(item.month)}</td>
                    <td className="py-1 text-right tabular-nums text-fg">{formatUsd(item.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <Modal isOpen={confirming} close={() => !unlocking && setConfirming(false)} title="Débloquer l'IA" size="sm">
        <p className="text-body-sm text-fg">
          Le client pourra de nouveau utiliser l'IA jusqu'à la fin du mois, au-delà de son seuil. Il repasse en orange
          tant que le seuil est dépassé. Confirmer le déblocage ?
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirming(false)} disabled={unlocking}>
            Annuler
          </Button>
          <Button variant="primary" loading={unlocking} onClick={unlock}>
            Débloquer
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function Section({ id, title, description, aside, children }) {
  return (
    <section
      id={`cf-section-${id}`}
      className="scroll-mt-20 mb-6 rounded-lg border border-line bg-surface shadow-e1"
    >
      <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-h2 text-fg">{title}</h2>
          {description && <p className="mt-0.5 text-caption text-fg-muted">{description}</p>}
        </div>
        {aside && <div className="shrink-0">{aside}</div>}
      </header>
      <div className="flex flex-col gap-4 p-5">{children}</div>
    </section>
  );
}

export default ClientForm;
